import type { Identifier } from "ra-core";

import type {
  CreateQuoteInput,
  PublicQuote,
  QuoterConfigResource,
  QuoterCreateQuoteInput,
  QuoterPanel,
  QuoterPreview,
  QuoterPreviewInput,
  QuoterReissueResult,
  QuoterTermImpact,
  ReserveUnitInput,
} from "../../types";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EQ (TASK-017): thin typed wrappers over the quoter RPCs
 * (TASK-015/016). Every function only forwards: the SECURITY DEFINER functions
 * are the authority (role checks, compliance guard, one active reservation per
 * unit, frozen snapshot) and raise Spanish messages that are surfaced as-is.
 * `getPublicQuote` is the only one callable without a session (anon key).
 */
const rpc = async <Result>(
  name: string,
  args: Record<string, unknown>,
): Promise<Result> => {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) {
    console.error(`${name}.error`, error);
    throw new Error(error.message || "No se pudo completar la acción");
  }
  return data as Result;
};

export const createSupabaseQuoterMethods = () => ({
  /** `create_quote()`: freezes the unit price and plan; returns the quote id. */
  createQuote: (input: CreateQuoteInput): Promise<Identifier> =>
    rpc<Identifier>("create_quote", {
      p_deal_id: input.dealId,
      p_unit_id: input.unitId,
      p_plan_template_id: input.planTemplateId,
      p_client_first_name: input.clientFirstName ?? null,
    }),

  /** `cancel_quote()`: cancels the quote and revokes its share link. */
  cancelQuote: (quoteId: Identifier): Promise<void> =>
    rpc<void>("cancel_quote", { p_quote_id: quoteId }),

  /** `create_quote_share_link()`: returns a new token (1 to 90 days, default 14). */
  createQuoteShareLink: (
    quoteId: Identifier,
    validDays?: number,
  ): Promise<string> =>
    rpc<string>("create_quote_share_link", {
      p_quote_id: quoteId,
      p_valid_days: validDays ?? 14,
    }),

  /** `revoke_quote_share_link()`. */
  revokeQuoteShareLink: (quoteId: Identifier): Promise<void> =>
    rpc<void>("revoke_quote_share_link", { p_quote_id: quoteId }),

  /** `reserve_unit()`: returns the reservation id. */
  reserveUnit: (input: ReserveUnitInput): Promise<Identifier> =>
    rpc<Identifier>("reserve_unit", {
      p_unit_id: input.unitId,
      p_deal_id: input.dealId,
      p_quote_id: input.quoteId ?? null,
    }),

  /** `release_unit_reservation()`. */
  releaseUnitReservation: (
    reservationId: Identifier,
    reason?: string,
  ): Promise<void> =>
    rpc<void>("release_unit_reservation", {
      p_reservation_id: reservationId,
      p_reason: reason ?? null,
    }),

  // Almikia Epic EQ2 (TASK-098): the definitive quoter RPCs (TASK-097). The
  // SQL engine is the authority; the FakeRest provider mirrors these.

  /** `quoter_preview()`: read-only price and calendar at the current list. */
  quoterPreview: (input: QuoterPreviewInput): Promise<QuoterPreview> =>
    rpc<QuoterPreview>("quoter_preview", {
      p_unit_id: input.unitId,
      p_scheme_id: input.schemeId ?? null,
      p_roof: input.roof ?? false,
      p_custom: input.custom ?? null,
    }),

  /** `quoter_create_quote()`: stores quote, details and the lead note; returns the quote id. */
  quoterCreateQuote: (input: QuoterCreateQuoteInput): Promise<Identifier> =>
    rpc<Identifier>("quoter_create_quote", {
      p_deal_id: input.dealId,
      p_unit_id: input.unitId,
      p_scheme_id: input.schemeId ?? null,
      p_roof: input.roof ?? false,
      p_custom: input.custom ?? null,
      p_client_first_name: input.clientFirstName ?? null,
    }),

  /** `quoter_reissue_quote()` (reexpedir): same quote at the current list. */
  quoterReissueQuote: (quoteId: Identifier): Promise<QuoterReissueResult> =>
    rpc<QuoterReissueResult>("quoter_reissue_quote", { p_quote_id: quoteId }),

  /** `quoter_reserve_quote()` (apartar): returns the reservation id. */
  quoterReserveQuote: (quoteId: Identifier): Promise<Identifier> =>
    rpc<Identifier>("quoter_reserve_quote", { p_quote_id: quoteId }),

  /** `quoter_sell_quote()` (vender): signs the operation, returns its id. */
  quoterSellQuote: (quoteId: Identifier, signedOn?: string): Promise<string> =>
    rpc<string>("quoter_sell_quote", {
      p_quote_id: quoteId,
      p_signed_on: signedOn ?? null,
    }),

  /** `quoter_term_change_impact()` ("ver impacto", plan editors of the project). */
  quoterTermChangeImpact: (
    projectId: Identifier,
    newTerm: number,
  ): Promise<QuoterTermImpact> =>
    rpc<QuoterTermImpact>("quoter_term_change_impact", {
      p_project_id: projectId,
      p_new_term: newTerm,
    }),

  /** `quoter_apply_term()`: plan editors of the project, requires `confirm = true`. */
  quoterApplyTerm: (
    projectId: Identifier,
    newTerm: number,
    confirm: boolean,
  ): Promise<QuoterTermImpact> =>
    rpc<QuoterTermImpact>("quoter_apply_term", {
      p_project_id: projectId,
      p_new_term: newTerm,
      p_confirm: confirm,
    }),

  /** `quoter_verify_unit()`: clears "por verificar" on one unit (direccion only). */
  quoterVerifyUnit: (unitId: Identifier): Promise<void> =>
    rpc<void>("quoter_verify_unit", { p_unit_id: unitId }),

  /** `quoter_verify_units()`: bulk; every unverified unit of the project when no ids. */
  quoterVerifyUnits: (
    projectId: Identifier,
    unitIds?: Identifier[],
  ): Promise<number> =>
    rpc<number>("quoter_verify_units", {
      p_project_id: projectId,
      p_unit_ids: unitIds ?? null,
    }),

  // Almikia batch EQ3 (TASK-114): custom-plan approval. The SQL functions are the authority
  // (only an active approver or direccion decides; direccion alone configures the approver).

  /** `quoter_approve_custom_plan()`: approves a custom plan pending approval. */
  quoterApproveCustomPlan: (quoteId: Identifier): Promise<void> =>
    rpc<void>("quoter_approve_custom_plan", { p_quote_id: quoteId }),

  /** `quoter_reject_custom_plan()`: the comment is mandatory. */
  quoterRejectCustomPlan: (
    quoteId: Identifier,
    comment: string,
  ): Promise<void> =>
    rpc<void>("quoter_reject_custom_plan", {
      p_quote_id: quoteId,
      p_comment: comment,
    }),

  /** `quoter_set_plan_approver()`: adds or (de)activates an approver (direccion only). */
  quoterSetPlanApprover: (
    salesId: Identifier,
    active: boolean,
  ): Promise<void> =>
    rpc<void>("quoter_set_plan_approver", {
      p_sales_id: salesId,
      p_active: active,
    }),

  /** `quoter_has_active_plan_approver()`: false means custom plans stay pending (direccion only). */
  quoterHasActivePlanApprover: (): Promise<boolean> =>
    rpc<boolean>("quoter_has_active_plan_approver", {}),

  /** `quoter_panel()`: list in force, sales missing, scarcity, models comparison. */
  quoterPanel: (projectId: Identifier): Promise<QuoterPanel | null> =>
    rpc<QuoterPanel | null>("quoter_panel", { p_project_id: projectId }),

  /**
   * Config write (insert / update, never delete). The five plan tables have no
   * insert / update grant (Almikia batch EQ3, TASK-113): they go through the
   * `quoter_save_config()` RPC, which checks role + project scope and audits.
   * `quoter_unit_details` is still written directly (direccion only, by RLS).
   */
  async saveQuoterConfig(
    resource: QuoterConfigResource,
    id: Identifier | null,
    data: Record<string, unknown>,
  ): Promise<{ id: Identifier }> {
    if (resource !== "quoter_unit_details") {
      const savedId = await rpc<Identifier>("quoter_save_config", {
        p_resource: resource,
        p_id: id,
        p_data: data,
      });
      return { id: savedId };
    }
    const table = getSupabaseClient().from(resource);
    const { data: saved, error } =
      id == null
        ? await table.insert(data).select("id").single()
        : await table.update(data).eq("id", id).select("id").single();
    if (error) {
      console.error(`${resource}.save.error`, error);
      throw new Error(error.message || "No se pudo completar la acción");
    }
    return saved as { id: Identifier };
  },

  /**
   * `get_public_quote()`: works without a session. Returns null for an
   * invalid, expired, revoked or cancelled token.
   */
  async getPublicQuote(token: string): Promise<PublicQuote | null> {
    const data = await rpc<PublicQuote | null>("get_public_quote", {
      p_token: token,
    });
    return data ?? null;
  },
});
